import { useCallback, useEffect, useRef, useState } from "react";
import { reviewApi, ReviewApiError } from "./api";
const prefix = "reviewed.private-draft.";
type Recovery = { body: string; base: number; at: number };
export const draftKey = (reviewId: string, attorneyId: string) =>
  `${prefix}${attorneyId}.${reviewId}`;
export function readRecovery(key: string): Recovery | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || "null");
    if (
      typeof value?.body === "string" &&
      Number.isInteger(value.base) &&
      Date.now() - value.at < 86400000
    )
      return value;
    sessionStorage.removeItem(key);
  } catch {
    /* storage is optional; leaving is guarded */
  }
  return null;
}
export function useDraftSafety({
  id,
  data,
  draft,
  saved,
  version,
  setDraft,
  setSaved,
  setVersion,
  onAccessError,
}: any) {
  const [state, setState] = useState(""),
    [conflict, setConflict] = useState<any>(null),
    [failed, setFailed] = useState(false);
  const key =
    data?.viewer === "attorney"
      ? draftKey(id, data.review.assigned_attorney_id)
      : null;
  const allowed =
    !!key &&
    !data.review.closed_at &&
    data.review.status !== "cancelled" &&
    (!data.review.proposal_state ||
      ["legacy", "agreed"].includes(data.review.proposal_state));
  const latest = useRef<any>();
  latest.current = { id, key, draft, saved, version, allowed };
  const inFlight = useRef(false),
    generation = useRef(0);
  useEffect(() => {
    const run = ++generation.current;
    setConflict(null);
    setFailed(false);
    setState("");
    return () => {
      generation.current = run + 1;
    };
  }, [id, key]);
  const remember = useCallback((body: string, base: number) => {
    if (!latest.current.key) return false;
    try {
      sessionStorage.setItem(
        latest.current.key,
        JSON.stringify({ body, base, at: Date.now() })
      );
      return true;
    } catch {
      return false;
    }
  }, []);
  const change = (body: string) => {
    const recovered = remember(body, version);
    setDraft(body);
    setFailed(false);
    setState(
      recovered
        ? "Waiting to save…"
        : "Browser recovery is unavailable. Keep this page open until saved."
    );
  };
  const save = useCallback(async () => {
    const current = latest.current,
      run = generation.current;
    if (
      inFlight.current ||
      !current.allowed ||
      current.draft === current.saved ||
      conflict
    )
      return;
    inFlight.current = true;
    setState("Saving privately…");
    setFailed(false);
    try {
      const result = await reviewApi(`/${current.id}/draft`, "PUT", {
        body: current.draft,
        version: current.version,
      });
      if (run !== generation.current) return;
      setVersion(result.draft.version);
      setSaved(result.draft.body);
      // Never overwrite text typed while this request was in flight.
      if (latest.current.draft === result.draft.body) {
        try {
          sessionStorage.removeItem(current.key);
        } catch {}
        setState("Saved privately");
      } else {
        remember(latest.current.draft, result.draft.version);
        setState("Waiting to save…");
      }
    } catch (e: any) {
      if (run !== generation.current) return;
      setFailed(true);
      setState(`Not saved to server: ${e.message}`);
      if (e instanceof ReviewApiError && e.status === 409) {
        try {
          const fresh = await reviewApi(`/${current.id}`);
          if (run === generation.current) setConflict(fresh.draft);
        } catch {}
      } else if (
        e instanceof ReviewApiError &&
        [401, 403, 404, 503].includes(e.status)
      )
        onAccessError(e);
    } finally {
      inFlight.current = false;
    }
  }, [conflict, onAccessError, remember, setSaved, setVersion]);
  useEffect(() => {
    if (!allowed || draft === saved || conflict || failed) return;
    const timer = setTimeout(() => void save(), 1000);
    return () => clearTimeout(timer);
  }, [allowed, draft, saved, version, conflict, failed, save]);
  useEffect(() => {
    if (!key || draft === saved) return;
    const unload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const guard = (e: MouseEvent) => {
      const link = (e.target as Element)?.closest?.("a[href]");
      if (
        link &&
        !remember(latest.current.draft, latest.current.version) &&
        !window.confirm(
          "Your private draft has not been saved and recovery storage is unavailable. Leave and discard these edits?"
        )
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const currentIndex = window.history.state?.idx;
    let restoring = false;
    const guardHistory = (e: PopStateEvent) => {
      if (restoring) {
        restoring = false;
        e.stopImmediatePropagation();
        return;
      }
      if (remember(latest.current.draft, latest.current.version)) return;
      const nextIndex = e.state?.idx;
      if (
        Number.isInteger(currentIndex) &&
        Number.isInteger(nextIndex) &&
        currentIndex !== nextIndex &&
        !window.confirm(
          "Your private draft has not been saved and recovery storage is unavailable. Leave and discard these edits?"
        )
      ) {
        restoring = true;
        e.stopImmediatePropagation();
        window.history.go(currentIndex - nextIndex);
      }
    };
    window.addEventListener("beforeunload", unload);
    document.addEventListener("click", guard, true);
    window.addEventListener("popstate", guardHistory, true);
    return () => {
      window.removeEventListener("beforeunload", unload);
      document.removeEventListener("click", guard, true);
      window.removeEventListener("popstate", guardHistory, true);
    };
  }, [key, draft, saved, remember]);
  function resolve(useLocal: boolean) {
    if (!conflict) return;
    setVersion(conflict.version);
    setSaved(conflict.body);
    if (!useLocal) {
      setDraft(conflict.body);
      try {
        sessionStorage.removeItem(key!);
      } catch {}
    } else remember(draft, conflict.version);
    setConflict(null);
    setFailed(false);
    setState(
      useLocal
        ? "Your recovered text is ready to save."
        : "Loaded saved version."
    );
  }
  return {
    change,
    save,
    state,
    conflict,
    setConflict,
    resolve,
    failed,
    saving: state === "Saving privately…",
  };
}
